<template>
  <v-avatar :size="`${size}px`" class="avatar" :style="avatarStyle">
    <img v-if="getImg" alt="Avatar" :src="getImg" @error="onImageError()" :width="size" :height="size" />
    <span v-else-if="fullName" class="avatar-initials" :style="initialsSpanStyle">{{ initials || '?' }}</span>
    <v-icon v-else size="large">mdi-account-circle</v-icon>
    <slot />
  </v-avatar>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { concatInitials, getBgColor, getColor } from '@/utils';

const props = withDefaults(defineProps<{ img?: string; fullName?: string; size?: number }>(), {
  img: undefined,
  fullName: undefined,
  size: 36,
});
const failed = ref(false);
const retryCount = ref(0);
const retrySuffix = ref('');
watch(
  () => props.img,
  () => {
    failed.value = false;
    retryCount.value = 0;
    retrySuffix.value = '';
  },
);
const getImg = computed<undefined | string>(() => {
  if (failed.value || !props.img) {
    return undefined;
  }
  return retrySuffix.value ? `${props.img}${props.img.includes('?') ? '&' : '?'}${retrySuffix.value}` : props.img;
});
const initials = computed<undefined | string>(() => props.fullName && concatInitials(props.fullName));
const initialsSpanStyle = computed<Record<string, string>>(() => ({
  'font-size': `${Math.floor(props.size / 2.5)}px`,
  'line-height': `${props.size}px`,
  color: getColor(props.fullName),
}));
const avatarStyle = computed<Record<string, string>>(() => ({
  'background-color': getBgColor(props.fullName),
  border: getBgColor(props.fullName) === '#FFFFFF' ? '1px solid #d2d2d7' : '',
}));

const onImageError = (): void => {
  if (retryCount.value < 3) {
    retryCount.value++;
    setTimeout(() => {
      retrySuffix.value = `_r=${Date.now()}`;
    }, 2000);
  } else {
    failed.value = true;
    console.warn(`Warning: can not load image ${props.img}`);
  }
};
</script>
